/**
 * "Sign in again first" - the backend's answer when a change to how the account
 * signs in comes from a session that signed in too long ago.
 *
 * It is a 403, but it is not "you may not": the same person may, after proving
 * again that they are who the token says. So it is told apart from an ordinary
 * refusal and answered with a way forward rather than a generic failure.
 */

import { useAuthStore } from "@/stores/authStore";

export const REAUTH_REQUIRED = "REAUTH_REQUIRED";

export const REAUTH_MESSAGE =
  "For your security, sign in again to change your sign-in methods.";

/** Whether a failed request was refused for want of a recent sign-in. */
export function isReauthRequired(error: unknown): boolean {
  const response = (
    error as {
      response?: { status?: number; data?: { error?: { code?: string } } };
    } | null
  )?.response;
  return (
    response?.status === 403 && response.data?.error?.code === REAUTH_REQUIRED
  );
}

/** Same shape as the payload, for the response interceptor, which only has that. */
export function isReauthRequiredPayload(data: unknown): boolean {
  return (
    (data as { error?: { code?: string } } | null)?.error?.code ===
    REAUTH_REQUIRED
  );
}

/**
 * Ends the session and sends the user to the login page, coming back to where
 * they were afterwards through the login page's existing `returnTo`.
 *
 * Signing out rather than refreshing: a refreshed token carries the session's
 * original sign-in time, so only a new sign-in satisfies the check.
 */
export async function signInAgain(returnTo: string): Promise<void> {
  // Awaited: logout clears the device, and the page load below would cut
  // that short.
  await useAuthStore.getState().logout();
  window.location.assign(
    `/traveler/login?returnTo=${encodeURIComponent(returnTo)}`,
  );
}
